<script setup>
import SearchKeyword from '@/Components/Search/Keyword.vue';
import TabList from '@/Components/Tab/TabList.vue';
import Heading from '@/Components/Heading.vue';
import SearchFilters from '@/Components/Search/Filters.vue';
import { ref, computed, onMounted, nextTick } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { THEMES, EVENT } from '@/constants';
import Button from '@/Components/Button.vue';
import useAnalytics from '@/composables/useAnalytics';

const analytics = useAnalytics();

const page = usePage();
const context = computed(() => page.props?.context);
const theme = context.value.theme;

const activeTab = defineModel({type: String});

const placeholderOptions = {
  courses: 'Enter course search keywords...(e.g. Business Management)',
  providers: 'Enter name of University, College or School',
  scholarships: 'Enter name of scholarship or field of study',
  careers: 'Enter name of career',
};

const keywordPlaceholder = computed(() => {
  return placeholderOptions[activeTab.value] || 'Search by keywords';
});

const props = defineProps({
  queryObject: {
    type: Object,
    required: true,
  }
});

const searchInput = ref({
  query: '',
  provider_type: [],
  outside_australia: '',
  states: [],
  countries: [],
  modes: [],
  tuition_fee: '',
  area_of_study: [],
  level_of_study: [],
  nationality: [],
  school_sectors: [],
  genders: [],
  industry: []
});

const tabs = ref([
  {
    label: 'Courses',
    value: 'courses',
  },
  {
    label: 'Providers',
    value: 'providers',
  },
  {
    label: 'Scholarships',
    value: 'scholarships',
  },
  {
    label: 'Careers',
    value: 'careers',
  },
]);

const filtersElement = ref(null);

const setSearchInput = () => {
  for (const [key, value] of Object.entries(props.queryObject)) {
    if (typeof searchInput.value[key] == 'string') {
      searchInput.value[key] = (value !== null) ? decodeURIComponent(value) : '';
    }

    if (typeof searchInput.value[key] == 'object') {
      searchInput.value[key] = decodeURIComponent(value).split(',');
    }
  }
};

onMounted(() => {
  setSearchInput();
});

const resetFilters = () => {
  searchInput.value.query = '';
  searchInput.value.provider_type = [];
  searchInput.value.states = [];
  searchInput.value.countries = [];
  searchInput.value.modes = [];
  searchInput.value.tuition_fee = '';
  searchInput.value.area_of_study = [];
  searchInput.value.level_of_study = [];
  searchInput.value.nationality = [];
  searchInput.value.school_sectors = [];
  searchInput.value.genders = [];
  searchInput.value.industry = [];
  searchInput.value.outside_australia = '';
};

const search = () => {
  let query = "page=1";
  const eventProperties = {};

  for (const [key, value] of Object.entries(searchInput.value)) {
    if ((typeof value === "object" && value.length) || (typeof value === "string" && value != "")) {
      eventProperties[key] = (typeof value === "object") ? Object.values(value) : value;
      query += `&${key}=${encodeURIComponent(value)}`;
    }
  }

  analytics.track(EVENT.ACIR_SEARCH_BUTTON_CLICKED, { ...eventProperties, tab: activeTab.value });

  router.get(`/${activeTab.value}?${query}`, {}, {
    preserveScroll: true
  });
};

const clearResults = () => {
  resetFilters();
  nextTick(search);
};

const setQuery = (option) => {
  searchInput.value.query = option.label;
  const [key, value] = option.value.split(':');
  if (Array.isArray(searchInput.value[key])) {
    // an option may map to several filter values, e.g. primary school teaching courses
    const values = value.split(',').filter(v => !searchInput.value[key].includes(v));
    searchInput.value[key].push(...values);
  } else {
    searchInput.value[key] = value;
  }
};

const headingClasses = computed(() => [
  'mb-6',
  theme == THEMES.STUDY_AUS && 'font-display-light text-primary-600',
  theme == THEMES.STUDY_MELB && 'md:text-[34px] font-display-medium font-bold text-white',
]);

const tabClasses = computed(() => [
  'mb-3',
  theme == THEMES.STUDY_MELB && 'text-white',
]);

const buttonVariant = (theme) => {
  return {
    [THEMES.STUDY_AUS]: 'primary',
    [THEMES.STUDY_MELB]: 'search',
  }[theme];
};

const buttonDiv = computed(() => [
  'col-span-1',
  theme == THEMES.STUDY_MELB && 'ml-2',
]);

</script>

<template>
  <section>
    <div class="max-w-screen-lg container">
      <Heading
        size="h1"
        style-size="h1"
        :class="headingClasses"
      >
        Search results
      </Heading>
      <TabList
        v-model="activeTab"
        :class="tabClasses"
        :tabs="tabs"
        @update="resetFilters"
      />
      <div class=" grid grid-cols-1 md:grid-cols-7 gap-2" :data-testid="`${activeTab}-filter`">
        <div class="col-span-1 md:col-span-6">
            <SearchKeyword
              v-model="searchInput.query"
              class="flex-1 mb-3"
              :placeholder="keywordPlaceholder"
              :active-tab="activeTab"
              @autocomplete-selected="setQuery"
              @search="search"
            />
          <SearchFilters
            ref="filtersElement"
            v-model="searchInput"
            :active-tab="activeTab"
            @clear="clearResults"
          />
        </div>
        <div :class="buttonDiv">
          <Button
            class="w-full md:w-fit"
            :variant="buttonVariant(theme)"
            @click="search"
          >
            Search
          </Button>
        </div>
      </div>
    </div>
  </section>
</template>
